// 구글 광고로 들어온 방문자인지 판별한다.
// 광고 링크의 utm 꼬리표는 첫 화면 주소에만 붙어 있어서, 앱 시작 때 sessionStorage 에 메모해 둔다.
// (다른 페이지를 다녀와도 유지되고, 탭을 닫으면 사라진다)
// Also covers AI-tool visitors (ChatGPT, Gemini, Claude, ...): links with utm_medium=ai. The "googleAd" name is kept.

// 구글 광고 링크의 utm_source 값들 (소문자). 새 구글 캠페인이 생기면 여기에 추가한다.
const GOOGLE_AD_SOURCES = ["google", "googlesa", "gdn", "googledgv", "googlecpv"];

// AI-tool links are detected by utm_medium=ai, whatever utm_source says
const AI_UTM_MEDIUM = "ai";

const STORE_KEY = "google_ad_utm";
// Case info carried in the AI link — only used to prefill the form, never sent as separate fields
const AI_CASE_STORE_KEY = "ai_case_prefill";
// Marks that the lead form should be auto-scrolled to once for this AI visit
const AI_SCROLL_STORE_KEY = "ai_case_scroll_pending";

// Max length per AI link param. Longer values are cut, never rejected (AI-made links may be messy)
const AI_CASE_MAX_LENGTH = { claim: 30, amount: 20, debtor: 10, note: 100 } as const;

// 서버에 상담 신청과 함께 보내는 광고 유입 정보
export type GoogleAdUtm = {
  utmSource: string;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  landingUrl: string;
};

// Case info from an AI link (null when missing)
export type AiCasePrefill = {
  claim: string | null;
  amount: string | null;
  debtor: string | null;
  note: string | null;
};

const isAiMedium = (medium: string | null) => medium?.trim().toLowerCase() === AI_UTM_MEDIUM;

// Collapse whitespace, trim and cut to maxLength. Empty -> null
const cleanParam = (value: string | null, maxLength: number): string | null => {
  const cleaned = value?.replace(/\s+/g, " ").trim().slice(0, maxLength).trim();
  return cleaned || null;
};

const readAiCase = (query: URLSearchParams): AiCasePrefill => ({
  claim: cleanParam(query.get("claim"), AI_CASE_MAX_LENGTH.claim),
  amount: cleanParam(query.get("amount"), AI_CASE_MAX_LENGTH.amount),
  debtor: cleanParam(query.get("debtor"), AI_CASE_MAX_LENGTH.debtor),
  note: cleanParam(query.get("note"), AI_CASE_MAX_LENGTH.note),
});

// 앱 시작 시 1번 호출: 주소에 구글 광고 꼬리표가 있으면 메모한다.
export const captureGoogleAdUtm = () => {
  try {
    const query = new URLSearchParams(window.location.search);
    const source = query.get("utm_source");
    const medium = query.get("utm_medium");
    const isAi = isAiMedium(medium);
    if (!source && !isAi) return; // 꼬리표가 없으면 이전 메모를 그대로 둔다

    // 새 광고 클릭 → 이전 메모를 교체. 구글이 아닌 광고로 다시 들어오면 메모를 지운다.
    sessionStorage.removeItem(AI_CASE_STORE_KEY);
    sessionStorage.removeItem(AI_SCROLL_STORE_KEY);
    if (!isAi && !GOOGLE_AD_SOURCES.includes((source ?? "").toLowerCase())) {
      sessionStorage.removeItem(STORE_KEY);
      return;
    }

    const utm: GoogleAdUtm = {
      // AI link without utm_source -> record "ai" (other)
      utmSource: source || AI_UTM_MEDIUM,
      utmMedium: medium,
      utmCampaign: query.get("utm_campaign"),
      utmTerm: query.get("utm_term"),
      utmContent: query.get("utm_content"),
      landingUrl: window.location.href,
    };
    sessionStorage.setItem(STORE_KEY, JSON.stringify(utm));

    if (isAi) {
      sessionStorage.setItem(AI_CASE_STORE_KEY, JSON.stringify(readAiCase(query)));
      sessionStorage.setItem(AI_SCROLL_STORE_KEY, "1");
    }
  } catch {
    // 저장소가 막힌 브라우저에서는 조용히 넘어간다
  }
};

// 메모해 둔 구글 광고 꼬리표. 구글 광고 유입이 아니면 null (AI 유입도 여기에 포함)
export const getGoogleAdUtm = (): GoogleAdUtm | null => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
  } catch {
    return null;
  }
};

// Case info from the AI link, or null when the visit is not from an AI tool
export const getAiCasePrefill = (): AiCasePrefill | null => {
  try {
    return JSON.parse(sessionStorage.getItem(AI_CASE_STORE_KEY) || "null");
  } catch {
    return null;
  }
};

// True once per AI visit (then cleared), so the auto-scroll happens only on the first landing
export const consumeAiScrollPending = (): boolean => {
  try {
    const pending = sessionStorage.getItem(AI_SCROLL_STORE_KEY) === "1";
    sessionStorage.removeItem(AI_SCROLL_STORE_KEY);
    return pending;
  } catch {
    return false;
  }
};
